import React, { type CSSProperties, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { footerIcons } from "../config";
import { FooterIcon } from "./icon";
import { IonFooter, useIonRouter } from "@ionic/react";
import { getFooterActiveIndex } from "../config";
import { NavPill } from "@shared/ui/NavPill";
import { ROUTES } from "@shared/constants/routes";

export const NavFooter = () => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const nav = useIonRouter();
  const activeIndex = getFooterActiveIndex(location.pathname);
  const [visualIndex, setVisualIndex] = useState(activeIndex);
  const isProfileSurface = location.pathname === ROUTES.overall.profile;
  const footerSurfaceRgb = isProfileSurface ? "254, 254, 252" : "251, 251, 250";
  const fadeLayerStyle = {
    background: `linear-gradient(180deg, rgba(${footerSurfaceRgb}, 0) 0%, rgba(${footerSurfaceRgb}, 0.04) 24%, rgba(${footerSurfaceRgb}, 0.22) 48%, rgba(${footerSurfaceRgb}, 0.68) 76%, rgba(${footerSurfaceRgb}, 0.94) 100%)`,
  } satisfies CSSProperties;
  const glowLayerStyle = {
    background: `radial-gradient(ellipse at center, rgba(${footerSurfaceRgb}, 0.74) 0%, rgba(${footerSurfaceRgb}, 0.42) 46%, rgba(${footerSurfaceRgb}, 0) 78%)`,
  } satisfies CSSProperties;

  useEffect(() => {
    setVisualIndex(activeIndex);
  }, [activeIndex]);

  const handleSelect = (index: number, path: string) => {
    setVisualIndex(index);

    const targetUrl = new URL(path, window.location.origin);
    const isCurrentTarget =
      location.pathname === targetUrl.pathname &&
      location.search === targetUrl.search &&
      location.hash === targetUrl.hash;

    if (isCurrentTarget) {
      return;
    }

    nav.push(path, "forward", "push");
  };

  return (
    <IonFooter className="pointer-events-none fixed inset-x-0 bottom-0 z-[1000] overflow-visible border-0 bg-transparent px-0 pb-[calc(var(--app-safe-area-bottom)+10px)] pt-7 shadow-none [--background:transparent] [box-shadow:none]">
      <span
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 h-[226px]"
        style={fadeLayerStyle}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none fixed bottom-[calc(var(--app-safe-area-bottom)+2px)] left-1/2 h-[126px] w-[min(458px,calc(100%_-_10px))] -translate-x-1/2 rounded-full blur-[10px]"
        style={glowLayerStyle}
      />
      <NavPill ariaLabel={t("navigation.primary")}>
        {footerIcons.map((footerIcon, index) => {
          return (
            <FooterIcon
              key={footerIcon.icon}
              iconInfo={footerIcon}
              isActive={index === visualIndex}
              isCurrent={index === activeIndex}
              onSelect={() => handleSelect(index, footerIcon.path)}
            />
          );
        })}
      </NavPill>
    </IonFooter>
  );
};
